import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Plus, RefreshCw, Save, Trash2, Tv, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRY_REGIONS = ['NZ', 'AU', 'US', 'UK', 'CA'];
const REGION_LABEL = {
  NZ: 'New Zealand', AU: 'Australia', US: 'United States',
  UK: 'United Kingdom', CA: 'Canada',
};

const arraysEqual = (a, b) => {
  if (!a || !b || a.length !== b.length) return false;
  const sa = [...a].sort(); const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
};

const RegionChips = ({ value = [], onChange, idPrefix }) => {
  const set = new Set(value);
  const allSelected = COUNTRY_REGIONS.every((r) => set.has(r));

  const toggle = (r) => {
    const next = new Set(set);
    if (next.has(r)) next.delete(r); else next.add(r);
    onChange(COUNTRY_REGIONS.filter((c) => next.has(c)));
  };

  const setAll = () => onChange([...COUNTRY_REGIONS]);
  const clear = () => onChange([]);

  return (
    <div className="ssvc-chips">
      {COUNTRY_REGIONS.map((r) => (
        <button
          key={r}
          type="button"
          onClick={() => toggle(r)}
          className={`ssvc-chip ${set.has(r) ? 'selected' : ''}`}
          data-testid={`${idPrefix}-${r}`}
          title={REGION_LABEL[r]}
        >
          {r}
        </button>
      ))}
      <button
        type="button"
        onClick={allSelected ? clear : setAll}
        className="ssvc-chip-mini"
        data-testid={`${idPrefix}-toggle-all`}
        title={allSelected ? 'Clear all' : 'Select all 5 (Global)'}
      >
        {allSelected ? 'Clear' : 'Global'}
      </button>
    </div>
  );
};

const AdminStreamingServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);

  // New service inline form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRegions, setNewRegions] = useState(['NZ']);
  const [newPreset, setNewPreset] = useState(true);
  const [creating, setCreating] = useState(false);

  // Edits-in-progress per service id
  const [edits, setEdits] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/streaming-services`, { withCredentials: true });
      setServices(res.data.services || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return services.filter((s) => {
      const regions = s.regions || [];
      if (filter !== 'all' && !regions.includes(filter)) return false;
      if (term && !(s.name || '').toLowerCase().includes(term)) return false;
      return true;
    });
  }, [services, filter, search]);

  const counts = useMemo(() => {
    const out = { all: services.length };
    COUNTRY_REGIONS.forEach((r) => { out[r] = 0; });
    services.forEach((s) => {
      (s.regions || []).forEach((r) => { if (out[r] !== undefined) out[r] += 1; });
    });
    return out;
  }, [services]);

  const setEdit = (id, patch) => {
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const hasEdit = (svc) => {
    const e = edits[svc.id];
    if (!e) return false;
    if (e.name !== undefined && e.name !== svc.name) return true;
    if (e.is_preset !== undefined && e.is_preset !== !!svc.is_preset) return true;
    if (e.regions !== undefined && !arraysEqual(e.regions, svc.regions || [])) return true;
    return false;
  };

  const saveEdit = async (svc) => {
    const e = edits[svc.id];
    if (!e || !hasEdit(svc)) return;
    const payload = {};
    if (e.name !== undefined && e.name !== svc.name) payload.name = e.name.trim();
    if (e.regions !== undefined && !arraysEqual(e.regions, svc.regions || [])) {
      if (!e.regions.length) { toast.error('Pick at least one region'); return; }
      payload.regions = e.regions;
    }
    if (e.is_preset !== undefined && e.is_preset !== !!svc.is_preset) payload.is_preset = e.is_preset;

    setSavingId(svc.id);
    try {
      const res = await axios.put(
        `${API}/admin/streaming-services/${svc.id}`,
        payload,
        { withCredentials: true }
      );
      setServices((prev) => prev.map((s) => (s.id === svc.id ? res.data : s)));
      setEdits((prev) => { const next = { ...prev }; delete next[svc.id]; return next; });
      toast.success(`Updated ${res.data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingId(null);
    }
  };

  const deleteSvc = async (svc) => {
    const usage = svc.usage_count || 0;
    const msg = usage > 0
      ? `Delete "${svc.name}"? It is currently tagged on ${usage} show${usage === 1 ? '' : 's'} — those tags will be cleared.`
      : `Delete "${svc.name}"?`;
    if (!window.confirm(msg)) return;

    setDeletingId(svc.id);
    try {
      await axios.delete(`${API}/admin/streaming-services/${svc.id}`, { withCredentials: true });
      setServices((prev) => prev.filter((s) => s.id !== svc.id));
      toast.success(`Deleted ${svc.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const createSvc = async () => {
    const name = newName.trim();
    if (!name) { toast.error('Name is required'); return; }
    if (!newRegions.length) { toast.error('Pick at least one region'); return; }
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/admin/streaming-services`,
        { name, regions: newRegions, is_preset: newPreset },
        { withCredentials: true }
      );
      setServices((prev) => [...prev, res.data].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setNewName('');
      setNewRegions(['NZ']);
      setNewPreset(true);
      setShowAddForm(false);
      toast.success(`Added ${res.data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const syncTmdb = async () => {
    if (!window.confirm(
      'Sync providers from TMDB?\n\nFor each region (NZ, AU, US, UK, CA) we\'ll fetch the official provider list and merge regions for any matching service. New providers we don\'t recognise will be inserted.\n\nThis is non-destructive — it never removes regions or services.'
    )) return;

    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/streaming-services/sync-tmdb`, {}, { withCredentials: true });
      const { inserted, updated, providers_seen, errors } = res.data;
      toast.success(`Sync complete: ${inserted} new, ${updated} updated (${providers_seen} providers seen)`);
      if (errors && errors.length) {
        toast(`Some regions had partial errors: ${errors.length}`, { icon: '⚠️' });
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'TMDB sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card className="scheduler-card" data-testid="admin-streaming-card">
      <CardHeader>
        <CardTitle className="scheduler-title">
          <Tv size={20} />
          Streaming Services ({services.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="tool-description" style={{ marginBottom: '1rem', color: '#999' }}>
          Each service can be tagged with multiple regions. Users see services tagged for{' '}
          <strong>their region</strong>. The <em>Global</em> button quick-selects all 5 regions.
        </p>

        {/* Filter pills */}
        <div className="ssvc-filters" data-testid="admin-streaming-filters">
          {['all', ...COUNTRY_REGIONS].map((r) => (
            <button
              key={r}
              className={`ssvc-pill ${filter === r ? 'active' : ''}`}
              onClick={() => setFilter(r)}
              data-testid={`ssvc-filter-${r}`}
            >
              {r === 'all' ? 'All' : REGION_LABEL[r]}
              <span className="ssvc-pill-count">{counts[r] ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="ssvc-toolbar">
          <Input
            type="text"
            placeholder="Search services..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ssvc-search"
            data-testid="ssvc-search"
          />
          <Button
            onClick={() => setShowAddForm((s) => !s)}
            data-testid="ssvc-toggle-add"
            className="ssvc-add-toggle"
          >
            {showAddForm ? <><X size={14} /> Cancel</> : <><Plus size={14} /> Add Service</>}
          </Button>
          <Button
            variant="outline"
            onClick={syncTmdb}
            disabled={syncing}
            data-testid="ssvc-sync-tmdb"
            className="ssvc-sync-btn"
            title="Pull official provider availability from TMDB"
          >
            {syncing ? <><Loader2 size={14} className="spinning" /> Syncing…</> : <><RefreshCw size={14} /> Sync from TMDB</>}
          </Button>
        </div>

        {showAddForm && (
          <div className="ssvc-add-form" data-testid="ssvc-add-form">
            <Input
              type="text"
              placeholder="Service name (e.g. Disney+)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={50}
              data-testid="ssvc-new-name"
            />
            <RegionChips value={newRegions} onChange={setNewRegions} idPrefix="ssvc-new-region" />
            <label className="ssvc-checkbox">
              <input
                type="checkbox"
                checked={newPreset}
                onChange={(e) => setNewPreset(e.target.checked)}
                data-testid="ssvc-new-preset"
              />
              Preset
            </label>
            <Button
              onClick={createSvc}
              disabled={creating || !newName.trim() || !newRegions.length}
              data-testid="ssvc-create-submit"
            >
              {creating ? <><Loader2 size={14} className="spinning" /> Creating…</> : <><Plus size={14} /> Create</>}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="ssvc-loading"><Loader2 className="spinning" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <p style={{ color: '#888', padding: '1rem 0' }}>No services match your filter.</p>
        ) : (
          <div className="ssvc-table-wrap">
            <table className="ssvc-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Regions</th>
                  <th>Preset</th>
                  <th>Usage</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((svc) => {
                  const e = edits[svc.id] || {};
                  const dirty = hasEdit(svc);
                  const currentRegions = e.regions !== undefined ? e.regions : (svc.regions || []);
                  return (
                    <tr key={svc.id} data-testid={`ssvc-row-${svc.id}`}>
                      <td>
                        <div className="ssvc-name-cell">
                          <Input
                            value={e.name !== undefined ? e.name : svc.name}
                            onChange={(ev) => setEdit(svc.id, { name: ev.target.value })}
                            maxLength={50}
                            className="ssvc-name-input"
                            data-testid={`ssvc-name-${svc.id}`}
                          />
                          {svc.tmdb_provider_id && (
                            <span className="ssvc-tmdb-badge" title={`TMDB provider id ${svc.tmdb_provider_id}`}>
                              TMDB
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <RegionChips
                          value={currentRegions}
                          onChange={(regions) => setEdit(svc.id, { regions })}
                          idPrefix={`ssvc-region-${svc.id}`}
                        />
                      </td>
                      <td className="ssvc-center">
                        <input
                          type="checkbox"
                          checked={e.is_preset !== undefined ? e.is_preset : !!svc.is_preset}
                          onChange={(ev) => setEdit(svc.id, { is_preset: ev.target.checked })}
                          data-testid={`ssvc-preset-${svc.id}`}
                        />
                      </td>
                      <td className="ssvc-center">{svc.usage_count || 0}</td>
                      <td>
                        <div className="ssvc-actions">
                          <Button
                            size="sm"
                            disabled={!dirty || savingId === svc.id}
                            onClick={() => saveEdit(svc)}
                            data-testid={`ssvc-save-${svc.id}`}
                          >
                            {savingId === svc.id ? <Loader2 size={12} className="spinning" /> : <Save size={12} />}
                            <span className="ssvc-action-label">Save</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={deletingId === svc.id}
                            onClick={() => deleteSvc(svc)}
                            data-testid={`ssvc-delete-${svc.id}`}
                          >
                            {deletingId === svc.id ? <Loader2 size={12} className="spinning" /> : <Trash2 size={12} />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <style>{`
          .ssvc-filters { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 0.85rem; }
          .ssvc-pill {
            display: inline-flex; align-items: center; gap: 0.4rem;
            background: #1a1a1a; color: #ccc; border: 1px solid #2a2a2a;
            padding: 0.35rem 0.7rem; border-radius: 999px;
            font-size: 0.78rem; cursor: pointer; transition: all 0.15s;
          }
          .ssvc-pill:hover { border-color: #444; color: #fff; }
          .ssvc-pill.active { background: #E62F88; color: #fff; border-color: #E62F88; }
          .ssvc-pill-count {
            background: rgba(255,255,255,0.15); border-radius: 999px;
            padding: 0 0.45rem; font-size: 0.7rem; min-width: 1.4rem; text-align: center;
          }
          .ssvc-pill.active .ssvc-pill-count { background: rgba(0,0,0,0.25); }

          .ssvc-toolbar { display: flex; gap: 0.5rem; margin-bottom: 0.85rem; align-items: center; flex-wrap: wrap; }
          .ssvc-search { max-width: 260px; }
          .ssvc-add-toggle { background: #E62F88 !important; border-radius: 8px !important; }
          .ssvc-sync-btn { border-radius: 8px !important; }

          .ssvc-add-form {
            display: flex; gap: 0.5rem; align-items: center;
            background: #161616; border: 1px solid #2a2a2a;
            border-radius: 10px; padding: 0.7rem; margin-bottom: 0.85rem;
            flex-wrap: wrap;
          }
          .ssvc-checkbox {
            color: #ccc; font-size: 0.8rem;
            display: inline-flex; align-items: center; gap: 0.35rem;
          }

          .ssvc-chips { display: inline-flex; flex-wrap: wrap; gap: 0.3rem; }
          .ssvc-chip {
            background: #1a1a1a; color: #888; border: 1px solid #2a2a2a;
            padding: 0.2rem 0.55rem; border-radius: 6px;
            font-size: 0.72rem; font-weight: 600; cursor: pointer; transition: all 0.12s;
            min-width: 2.6rem;
          }
          .ssvc-chip:hover { border-color: #555; color: #ccc; }
          .ssvc-chip.selected {
            background: rgba(230, 47, 136, 0.18); color: #ff7ab3; border-color: #E62F88;
          }
          .ssvc-chip-mini {
            background: transparent; color: #777; border: 1px dashed #444;
            padding: 0.2rem 0.55rem; border-radius: 6px;
            font-size: 0.7rem; cursor: pointer; transition: all 0.12s;
          }
          .ssvc-chip-mini:hover { color: #fff; border-color: #888; }

          .ssvc-tmdb-badge {
            background: #142a3a; color: #6aa9d6; border: 1px solid #1d3d56;
            font-size: 0.62rem; font-weight: 700; letter-spacing: 0.04em;
            padding: 0.05rem 0.32rem; border-radius: 4px;
          }
          .ssvc-name-cell { display: flex; gap: 0.4rem; align-items: center; }

          .ssvc-table-wrap { overflow-x: auto; }
          .ssvc-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
          .ssvc-table th, .ssvc-table td {
            padding: 0.55rem 0.6rem;
            border-bottom: 1px solid #1f1f1f;
            vertical-align: middle;
          }
          .ssvc-table th {
            text-align: left; color: #aaa; font-weight: 500;
            background: #131313; position: sticky; top: 0;
          }
          .ssvc-center { text-align: center; }
          .ssvc-name-input { min-width: 140px; max-width: 240px; }
          .ssvc-actions { display: flex; gap: 0.4rem; }
          .ssvc-actions button { border-radius: 8px !important; }
          .ssvc-action-label { display: none; margin-left: 0.3rem; }
          @media (min-width: 700px) {
            .ssvc-action-label { display: inline; }
          }
          .ssvc-loading { color: #aaa; padding: 1rem 0; display: flex; align-items: center; gap: 0.5rem; }
          .spinning { animation: ssvc-spin 1s linear infinite; }
          @keyframes ssvc-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>
      </CardContent>
    </Card>
  );
};

export default AdminStreamingServices;
